import React, { useEffect, useState } from "react";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Check, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { isNative, buyPremium, restorePurchases, getPlatform } from "../lib/native";

const FEATURES = [
  "Unlimited AI photorealistic try-ons",
  "Muse — your AI stylist for any occasion",
  "10 curated scenes + custom backgrounds",
  "Cross-store wardrobe from any URL",
  "Outfit archive with public share links",
  "Body measurements & fit preferences",
  "Priority image generation queue",
];

const Pricing = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/billing/status").then((r) => setStatus(r.data)).catch(()=>{});
  }, [user]);

  const startCheckout = async () => {
    if (!user) { nav("/auth?mode=signup"); return; }
    setLoading(true);
    try {
      // Native path (iOS/Android): use RevenueCat / Apple IAP / Play Billing
      if (isNative()) {
        const res = await buyPremium();
        if (res.ok) {
          toast.success(`Purchased via ${getPlatform() === "ios" ? "App Store" : "Play Billing"}`);
          // Backend will be updated by RevenueCat webhook; poll billing/status
          setTimeout(() => api.get("/billing/status").then((r) => setStatus(r.data)), 2500);
          return;
        }
        if (res.reason === "cancelled-or-failed") return; // silent — user aborted
        toast.error("Native purchase unavailable — falling back to web checkout");
      }
      // Web path (Stripe)
      const r = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
        lookup_key: "atelier_annual_gbp",
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(errText(e, "Could not start checkout"));
    } finally { setLoading(false); }
  };

  const restore = async () => {
    if (!isNative()) return;
    const res = await restorePurchases();
    if (res.ok) toast.success("Purchases restored");
    else toast.error("Nothing to restore");
    setTimeout(() => api.get("/billing/status").then((r) => setStatus(r.data)), 1500);
  };

  const daysLeft = status?.trial_days_left ?? 0;
  const isActive = status?.status === "active";
  const isTrialing = status?.status === "trialing";

  return (
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16 md:py-24">
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-6">
          <div className="font-mono-eyebrow">Membership</div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-none mt-3">
            One flat<br/><em className="text-[color:var(--accent)]">annual</em> fee.
          </h1>
          <p className="mt-8 max-w-md text-black/70 leading-relaxed">
            Seven days on us. After that, £9.99 for a full year of unlimited try-ons,
            Muse styling, and the private wardrobe you've been building.
          </p>
          <div className="mt-10 space-y-4">
            <div className="flex items-center gap-3 font-mono-eyebrow text-black/60">
              <span className="w-8 h-px bg-black/40"/>
              <span>Also on iOS · Google Play — same price, same account</span>
            </div>
            <div className="flex items-center gap-3 font-mono-eyebrow text-black/60">
              <span className="w-8 h-px bg-black/40"/>
              <span>Cancel anytime · no auto-charge during trial</span>
            </div>
            <div className="flex items-center gap-3 font-mono-eyebrow text-black/60">
              <span className="w-8 h-px bg-black/40"/>
              <span>Pay by card, Apple Pay or Google Pay</span>
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-6">
          <div className="relative bg-white border border-black p-10 md:p-12">
            <div className="absolute -top-3 left-8 bg-[color:var(--accent)] text-white px-3 py-1 font-mono-eyebrow">
              {isActive ? "You're a member" : isTrialing ? `Trial · ${daysLeft} day${daysLeft===1?"":"s"} left` : "30-day free trial"}
            </div>
            <div className="font-mono-eyebrow">Atelier Premium</div>
            <div className="flex items-end gap-3 mt-2">
              <div className="font-serif-display text-7xl leading-none">£9.99</div>
              <div className="pb-2 text-black/60">/ year</div>
            </div>
            <div className="mt-2 text-black/50">≈ 83p a month · billed annually</div>

            <ul className="mt-10 space-y-3">
              {FEATURES.map((f) => (
                <li key={f} className="flex gap-3">
                  <Check className="w-5 h-5 mt-0.5 shrink-0 text-[color:var(--accent)]"/>
                  <span className="leading-tight">{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10">
              {isActive ? (
                <Link to="/studio" data-testid="pricing-open-studio" className="btn-ink w-full block text-center">Open the Studio</Link>
              ) : (
                <button data-testid="pricing-checkout-btn" onClick={startCheckout} disabled={loading}
                  className="btn-ink w-full flex items-center justify-center gap-2 disabled:opacity-50">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin"/> Redirecting…</> :
                    (isTrialing ? "Continue after trial · £9.99" : "Start 30-day free trial")}
                </button>
              )}
              <div className="text-xs text-black/50 mt-3 text-center">
                {isTrialing ? "No charge today. Card required only at end of trial." :
                 isNative() ? `Billed through ${getPlatform() === "ios" ? "the App Store" : "Google Play"}` :
                 "Test mode — use card 4242 4242 4242 4242 · any future date · any CVC"}
              </div>
              {isNative() && (
                <button data-testid="restore-purchases" onClick={restore}
                  className="w-full mt-3 text-xs font-mono-eyebrow flex items-center justify-center gap-1.5 py-2 hover:bg-black/5 transition-colors">
                  <RotateCcw className="w-3 h-3"/> Restore purchases
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
